import { useMemo, useState } from "react";
import { useCrm, collabById } from "@/lib/crm";
import { useAuth } from "@/lib/auth";
import { eur, eur2, dateIt, compensoSsd, isAcq } from "@/lib/constants";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { BadgeCheck, TrendingUp, Wallet, Users2, MapPin, FileSpreadsheet, FileText, Filter, X } from "lucide-react";

export default function Clienti() {
  const { ssd, collaboratori, settings, api, loading } = useCrm();
  const { isTitolare } = useAuth();
  const comm = settings?.commissione ?? 0.5;
  const isRitr = settings?.settore === "ritrattistica";

  const [collab, setCollab] = useState("all");
  const [da, setDa] = useState("");
  const [a, setA] = useState("");
  const [busy, setBusy] = useState("");

  const { rows, totRicavo, totCompenso } = useMemo(() => {
    let chiusi = ssd.filter((s) => isAcq(s, settings));
    if (collab !== "all") chiusi = chiusi.filter((s) => (s.collaboratore_id || "") === collab);
    if (da) chiusi = chiusi.filter((s) => (s.data_acquisizione || "") >= da);
    if (a) chiusi = chiusi.filter((s) => (s.data_acquisizione || "") <= a);
    chiusi.sort((x, y) => (y.data_acquisizione || "").localeCompare(x.data_acquisizione || ""));
    const rows = chiusi.map((s) => {
      const c = collabById(collaboratori, s.collaboratore_id);
      return { ...s, collab: c, compenso: compensoSsd(s, c, comm, settings) };
    });
    return {
      rows,
      totRicavo: rows.reduce((acc, r) => acc + (r.ricavo || 0), 0),
      totCompenso: rows.reduce((acc, r) => acc + r.compenso, 0),
    };
  }, [ssd, collaboratori, comm, collab, da, a, settings]);

  const hasFilter = collab !== "all" || da || a;
  const resetFilters = () => { setCollab("all"); setDa(""); setA(""); };

  const params = () => ({ collaboratore_id: collab !== "all" ? collab : "", da, a });
  const doExport = async (kind) => {
    setBusy(kind);
    try {
      if (kind === "xlsx") await api.exportClienti(params());
      else await api.exportClientiPdf(params());
      toast.success(`Export ${kind === "xlsx" ? "Excel" : "PDF"} generato`);
    } catch {
      toast.error("Errore durante l'export");
    } finally {
      setBusy("");
    }
  };

  if (loading) return <div className="p-10 text-slate-400">Caricamento…</div>;

  return (
    <div className="space-y-8">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 dark:text-white flex items-center gap-2">
            <BadgeCheck className="h-7 w-7 text-emerald-500" /> Clienti Acquisiti
          </h1>
          <p className="text-slate-500 mt-1">Riepilogo di tutti i {isRitr ? "clienti acquisiti" : "SSD chiuse/acquisite"} dalla pipeline.</p>
        </div>
        {isTitolare && (
          <div className="flex items-center gap-2">
            <Button data-testid="export-clienti-excel-button" variant="outline" className="rounded-full gap-1.5" disabled={busy} onClick={() => doExport("xlsx")}>
              <FileSpreadsheet className="h-4 w-4" /> {busy === "xlsx" ? "Attendere…" : "Excel"}
            </Button>
            <Button data-testid="export-clienti-pdf-button" variant="outline" className="rounded-full gap-1.5" disabled={busy} onClick={() => doExport("pdf")}>
              <FileText className="h-4 w-4" /> {busy === "pdf" ? "Attendere…" : "PDF"}
            </Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card data-testid="clienti-kpi-count" className="p-5">
          <div className="flex items-center justify-between"><span className="text-xs uppercase tracking-wider text-slate-400">Clienti acquisiti</span><BadgeCheck className="h-4 w-4 text-emerald-500" /></div>
          <div className="text-3xl font-extrabold mt-2 tabular-nums text-slate-900 dark:text-white">{rows.length}</div>
        </Card>
        <Card data-testid="clienti-kpi-ricavi" className="p-5">
          <div className="flex items-center justify-between"><span className="text-xs uppercase tracking-wider text-slate-400">Ricavi contratti</span><TrendingUp className="h-4 w-4 text-sky-500" /></div>
          <div className="text-3xl font-extrabold mt-2 tabular-nums text-sky-600">{eur(totRicavo)}</div>
        </Card>
        <Card data-testid="clienti-kpi-compensi" className="p-5">
          <div className="flex items-center justify-between"><span className="text-xs uppercase tracking-wider text-slate-400">Compensi</span><Wallet className="h-4 w-4 text-primary" /></div>
          <div className="text-3xl font-extrabold mt-2 tabular-nums text-primary">{eur(totCompenso)}</div>
        </Card>
      </div>

      <Card className="p-4 sm:p-5">
        <div className="flex items-end gap-4 flex-wrap">
          <div className="flex items-center gap-1.5 text-sm font-medium text-slate-500 mb-2.5"><Filter className="h-4 w-4" /> Filtri</div>
          <div className="grid gap-1.5 min-w-[200px]">
            <Label className="text-xs">Collaboratore</Label>
            <Select value={collab} onValueChange={setCollab}>
              <SelectTrigger data-testid="clienti-filter-collab" className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i collaboratori</SelectItem>
                {collaboratori.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-1.5">
            <Label className="text-xs">Dal (acquisizione)</Label>
            <Input data-testid="clienti-filter-da" type="date" value={da} onChange={(e) => setDa(e.target.value)} className="h-9 w-40" />
          </div>
          <div className="grid gap-1.5">
            <Label className="text-xs">Al (acquisizione)</Label>
            <Input data-testid="clienti-filter-a" type="date" value={a} onChange={(e) => setA(e.target.value)} className="h-9 w-40" />
          </div>
          {hasFilter && (
            <Button data-testid="clienti-filter-reset" variant="ghost" size="sm" className="h-9 gap-1.5 text-slate-500" onClick={resetFilters}>
              <X className="h-3.5 w-3.5" /> Azzera
            </Button>
          )}
        </div>
      </Card>

      <Card className="p-0 overflow-hidden animate-fade-up">
        {rows.length === 0 ? (
          <div className="p-12 text-center text-sm text-slate-400">{hasFilter ? "Nessun cliente corrisponde ai filtri selezionati." : `Nessun cliente acquisito. Sposta un ${isRitr ? "cliente" : "a SSD"} nella fase "Chiuso" dalla Pipeline.`}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 text-left">
                  <th className="px-4 py-3 font-semibold text-slate-500 uppercase text-[11px] tracking-wider">Società</th>
                  <th className="px-4 py-3 font-semibold text-slate-500 uppercase text-[11px] tracking-wider">Disciplina</th>
                  <th className="px-4 py-3 font-semibold text-slate-500 uppercase text-[11px] tracking-wider">Collaboratore</th>
                  <th className="px-4 py-3 font-semibold text-slate-500 uppercase text-[11px] tracking-wider">Acquisizione</th>
                  <th className="px-4 py-3 font-semibold text-slate-500 uppercase text-[11px] tracking-wider text-right">Ricavo</th>
                  <th className="px-4 py-3 font-semibold text-slate-500 uppercase text-[11px] tracking-wider text-right">Compenso</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} data-testid={`cliente-row-${r.id}`} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-900/40 transition-colors">
                    <td className="px-4 py-3">
                      <div className="font-semibold text-slate-900 dark:text-white">{r.nome}</div>
                      {r.citta && <div className="text-[11px] text-slate-400 inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{r.citta}{r.atleti > 0 && <span className="ml-2 inline-flex items-center gap-1"><Users2 className="h-3 w-3" />{r.atleti}</span>}</div>}
                    </td>
                    <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{r.disciplina || "—"}</td>
                    <td className="px-4 py-3">
                      {r.collab ? (
                        <span className="inline-flex items-center gap-1.5">
                          <span className="h-5 w-5 rounded-full flex items-center justify-center text-[9px] font-bold text-white" style={{ background: r.collab.colore }}>{r.collab.nome.split(" ").map((n) => n[0]).slice(0, 2).join("")}</span>
                          <span className="text-slate-700 dark:text-slate-200">{r.collab.nome}</span>
                        </span>
                      ) : <Badge variant="outline">Titolare</Badge>}
                    </td>
                    <td className="px-4 py-3 text-slate-600 dark:text-slate-300 tabular-nums">{dateIt(r.data_acquisizione)}</td>
                    <td className="px-4 py-3 text-right font-semibold text-sky-600 tabular-nums">{eur2(r.ricavo)}</td>
                    <td className="px-4 py-3 text-right font-semibold text-primary tabular-nums">{eur2(r.compenso)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-slate-50 dark:bg-slate-900/50 font-bold">
                  <td className="px-4 py-3 text-slate-900 dark:text-white" colSpan={4}>TOTALE</td>
                  <td className="px-4 py-3 text-right text-sky-600 tabular-nums">{eur2(totRicavo)}</td>
                  <td className="px-4 py-3 text-right text-primary tabular-nums">{eur2(totCompenso)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
